import { useState, useRef, useEffect, useCallback } from 'react'
import { IconClose } from './components/Icons'
import './ExportModal.css'
import { useModalA11y } from './utils/useModalA11y'

const FILTERS = [
  { id: 'none', label: 'ללא' },
  { id: 'grayscale', label: 'אפור' },
  { id: 'sepia', label: 'סֶפִּיָה' },
  { id: 'warm', label: 'חם' },
  { id: 'cool', label: 'קר' },
  { id: 'contrast', label: 'ניגודיות' },
]

const FILTER_CSS = {
  grayscale: 'grayscale(1)',
  sepia: 'sepia(1)',
  warm: 'sepia(0.3) brightness(1.05) saturate(1.1)',
  cool: 'brightness(1.02) saturate(0.9) hue-rotate(10deg)',
  contrast: 'contrast(1.15)',
}

function getProcessedCanvas(source, filterId, crop) {
  const sw = source.width
  const sh = source.height
  const x = crop ? Math.round(sw * crop.x / 100) : 0
  const y = crop ? Math.round(sh * crop.y / 100) : 0
  const w = crop ? Math.round(sw * crop.w / 100) : sw
  const h = crop ? Math.round(sh * crop.h / 100) : sh
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const ctx = out.getContext('2d')
  if (filterId && filterId !== 'none' && FILTER_CSS[filterId]) ctx.filter = FILTER_CSS[filterId]
  ctx.drawImage(source, x, y, w, h, 0, 0, w, h)
  return out
}

export function ExportModal({ getCompositeCanvas, onApplyToCanvas, onClose }) {
  const [filter, setFilter] = useState('none')
  const [cropMargin, setCropMargin] = useState(0)
  const previewRef = useRef(null)
  const panelRef = useRef(null)
  useModalA11y({ onClose, modalRef: panelRef })

  const source = getCompositeCanvas?.()
  const hasCrop = cropMargin > 0
  const crop = hasCrop
    ? { x: cropMargin, y: cropMargin, w: 100 - 2 * cropMargin, h: 100 - 2 * cropMargin }
    : null

  const refreshPreview = useCallback(() => {
    const src = getCompositeCanvas?.()
    if (!src || !previewRef.current) return
    const out = getProcessedCanvas(src, filter, crop)
    const ctx = previewRef.current.getContext('2d')
    previewRef.current.width = out.width
    previewRef.current.height = out.height
    ctx.drawImage(out, 0, 0)
  }, [getCompositeCanvas, filter, crop, cropMargin])

  useEffect(() => {
    refreshPreview()
  }, [refreshPreview])

  if (!source) return null

  const handleExportPng = () => {
    const out = getProcessedCanvas(source, filter, crop)
    const link = document.createElement('a')
    link.download = `coloring-${Date.now()}.png`
    link.href = out.toDataURL('image/png')
    link.click()
    onClose?.()
  }

  const handleExportPdf = async () => {
    const out = getProcessedCanvas(source, filter, crop)
    const imgData = out.toDataURL('image/png')
    const w = out.width
    const h = out.height
    const { jsPDF } = await import('jspdf')
    const pdf = new jsPDF({
      orientation: w > h ? 'landscape' : 'portrait',
      unit: 'px',
      format: [w, h],
    })
    pdf.addImage(imgData, 'PNG', 0, 0, w, h)
    pdf.save(`coloring-${Date.now()}.pdf`)
    onClose?.()
  }

  const handleApplyToPicture = () => {
    const out = getProcessedCanvas(source, filter, crop)
    const dataUrl = out.toDataURL('image/png')
    onApplyToCanvas?.(dataUrl)
    onClose?.()
  }

  return (
    <div className="export-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="ייצוא עם פילטר וחיתוך">
      <div className="export-modal" ref={panelRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3>ייצוא - פילטר וחיתוך</h3>
          <button type="button" className="export-modal-close" onClick={onClose} aria-label="סגור">
            <IconClose size={22} />
          </button>
        </div>
        <div className="export-modal-preview-wrap">
          <canvas ref={previewRef} className="export-modal-preview" width={source.width} height={source.height} />
        </div>
        <div className="export-modal-controls">
          <label className="export-modal-label">
            <span>פילטר</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="export-modal-select">
              {FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </label>
          <label className="export-modal-label">
            <span>שוליים לחיתוך (%)</span>
            <input
              type="range"
              min="0"
              max="40"
              value={cropMargin}
              onChange={(e) => setCropMargin(Number(e.target.value))}
              className="export-modal-slider"
            />
            <span className="export-modal-value">{cropMargin}%</span>
          </label>
        </div>
        <div className="export-modal-actions">
          <button type="button" className="primary" onClick={handleApplyToPicture}>החל על התמונה</button>
          <button type="button" className="primary" onClick={handleExportPng}>שמור כתמונה</button>
          <button type="button" className="primary" onClick={handleExportPdf}>שמור כ-PDF</button>
          <button type="button" onClick={onClose}>ביטול</button>
        </div>
      </div>
    </div>
  )
}
